import React, { useMemo, useState, useRef, useEffect } from "react";
import { FaHeart, FaRegHeart, FaLock } from "react-icons/fa"; // Added FaLock icon
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { UserAuth } from "../context/AuthContext";
import { useContent } from "../context/ContentContext";
import { useSavedSeries } from "../context/SavedSeriesContext";
import { usePassword } from "../context/PasswordContext";
import { arrayUnion, arrayRemove, doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

// Update the component props to include liveEpisodes
const Row = ({ category, liveEpisodes = [] }) => {
  const { user } = UserAuth();
  const { series: allSeries } = useContent();
  const { savedSeries, saveSeries, removeSeries } = useSavedSeries();
  const [isOverflowing, setIsOverflowing] = useState(false);
  const sliderRef = useRef(null);
  const { checkSeriesAccess } = usePassword();
  const userDocRef = doc(db, "users", `${user?.uid}`);
  const navigate = useNavigate();
  const [loadedImages, setLoadedImages] = useState({});

  // State to track which series are password protected
  const [protectedSeries, setProtectedSeries] = useState({});

  // Filter series based on the category query
  // Filter and sort series by order for this category
  const sortedSeries = useMemo(() => {
    const scopedSeries = category
      ? allSeries.filter((s) => s.tags?.includes(category.query))
      : [];
    return [...scopedSeries].sort((a, b) => a.order - b.order);
  }, [allSeries, category]);

  useEffect(() => {
    if (sliderRef.current) {
      setIsOverflowing(
        sliderRef.current.scrollWidth > sliderRef.current.clientWidth,
      );
    }
  }, [sortedSeries]);

  // Check which series require passwords
  useEffect(() => {
    let isMounted = true; // Prevent state update after unmount

    const checkProtection = async () => {
      const protectionStatus = {};

      for (const muni of sortedSeries) {
        const linkUrl = `/series/${muni.id}`;
        const { isProtected, isAuthorized } = await checkSeriesAccess(
          muni.id,
          linkUrl,
          false,
        );

        if (isProtected) {
          protectionStatus[muni.id] = {
            isProtected: true,
            hasAccess: isAuthorized,
          };
        }
      }

      if (isMounted) {
        setProtectedSeries((prev) => {
          // Avoid unnecessary re-renders if the data hasn't changed
          if (JSON.stringify(prev) !== JSON.stringify(protectionStatus)) {
            return protectionStatus;
          }
          return prev;
        });
      }
    };

    if (sortedSeries.length > 0) {
      checkProtection();
    }

    return () => {
      isMounted = false; // Cleanup function
    };
  }, [sortedSeries, checkSeriesAccess]);

  const toggleSavedSeries = async (e, muni, linkUrl) => {
    e.stopPropagation(); // Prevent clicking the heart from triggering series navigation

    if (!user?.uid) {
      alert("Please log in to save a series");
      return;
    }

    const isAlreadySaved = savedSeries.some((s) => s.id === muni.id);

    if (isAlreadySaved) {
      // Remove from Firestore and Context
      await updateDoc(userDocRef, {
        savedSeries: arrayRemove({
          id: muni.id,
          title: muni.title,
          img: muni.thumbnail,
          linkUrl,
        }),
      });
      removeSeries(muni.id);
    } else {
      // Add to Firestore and Context
      await updateDoc(userDocRef, {
        savedSeries: arrayUnion({
          id: muni.id,
          title: muni.title,
          img: muni.thumbnail,
          linkUrl,
        }),
      });
      saveSeries({
        id: muni.id,
        title: muni.title,
        img: muni.thumbnail,
        linkUrl,
      });
    }
  };

  const handleSeriesClick = async (e, muni) => {
    e.preventDefault();
    const linkUrl = `/series/${muni.id}`;

    // Check if the series is password protected and if user has access
    const { isAuthorized } = await checkSeriesAccess(muni.id, linkUrl);

    // If authorized, navigate to the series page
    if (isAuthorized) {
      navigate(linkUrl);
    }
    // If not authorized, the PasswordContext will display the password modal
  };

  if (!category) return null;

  return (
    <>
      <div className="flex items-center justify-between gap-4 px-4 pt-4 pb-2">
        <div className="flex items-center gap-2">
          <h2 className="text-white font-bold md:text-xl">{category.name}</h2>
          {sortedSeries.filter((s) => s.tags?.includes("live")).length > 0 && (
            <span className="text-xs bg-red-600 text-white px-1.5 py-0.5 rounded font-bold">
              {sortedSeries.filter((s) => s.tags?.includes("live")).length} LIVE
            </span>
          )}
        </div>
        <button
          onClick={() =>
            navigate(`/category/${encodeURIComponent(category.query)}`)
          }
          className="text-xs md:text-sm text-yellow-300 hover:text-yellow-200 transition-colors"
          aria-label={`See all for ${category.name}`}
        >
          See All
        </button>
      </div>
      <div className="relative flex items-center group pl-2">
        {isOverflowing && (
          <MdChevronLeft
            onClick={() => (sliderRef.current.scrollLeft -= 500)}
            className="bg-yellow-400 left-0 rounded-full absolute opacity-50 hover:opacity-100 cursor-pointer z-10 hidden group-hover:block"
            size={40}
          />
        )}
        <div
          ref={sliderRef}
          className="w-full h-full overflow-x-scroll whitespace-nowrap scroll-smooth scrollbar-hide"
        >
          {sortedSeries.map((muni) => {
            // Check if series has "live" tag
            const isLive = muni.tags?.includes("live") || false;

            const linkUrl = `series/${muni.id}`;
            const isSaved = savedSeries.some((s) => s.id === muni.id);
            const isProtected = protectedSeries[muni.id]?.isProtected || false;
            const hasSeriesAccess =
              protectedSeries[muni.id]?.hasAccess || false;
            const needsLock = isProtected && !hasSeriesAccess;

            return (
              <div
                key={muni.id}
                className={`w-[160px] sm:w-[200px] md:w-[240px] lg:w-[280px] inline-block relative p-2 align-top`}
              >
                <div
                  onClick={(e) => handleSeriesClick(e, muni)}
                  className="cursor-pointer relative flex flex-col h-full"
                >
                  <div className="relative aspect-video rounded overflow-hidden bg-gray-800">
                    {!loadedImages[muni.id] && (
                      <div className="absolute inset-0 animate-pulse bg-gradient-to-br from-gray-800 to-gray-700" />
                    )}
                    <img
                      className={`w-full h-full object-cover block transition-opacity duration-300 ${
                        loadedImages[muni.id] ? "opacity-100" : "opacity-0"
                      }`}
                      src={muni.thumbnail}
                      alt={muni.title}
                      loading="lazy"
                      decoding="async"
                      onLoad={() =>
                        setLoadedImages((prev) => ({
                          ...prev,
                          [muni.id]: true,
                        }))
                      }
                    />
                    {/* Lock overlay for password protected content */}
                    {needsLock && (
                      <div className="absolute inset-0 bg-black/50 rounded flex flex-col justify-center items-center">
                        <FaLock className="text-yellow-400 text-2xl mb-2" />
                        <p className="text-white text-xs">Password Protected</p>
                      </div>
                    )}

                    {/* Standard hover overlay */}
                    <div className="absolute top-0 left-0 w-full h-full hover:bg-black/80 opacity-0 hover:opacity-100 text-white flex justify-center items-center rounded p-2">
                      <p className="text-white text-xs md:text-sm font-bold break-words text-center w-full max-w-full overflow-hidden">
                        {muni.title}
                      </p>
                    </div>

                    {/* Move the LIVE badge above the hover overlay for better visibility */}
                    {isLive && (
                      <div className="absolute top-2 right-2 z-30">
                        <p className="bg-red-600 text-white text-xs px-2 py-1 rounded font-bold">
                          LIVE
                        </p>
                      </div>
                    )}
                  </div>

                  <p className="whitespace-normal text-xs md:text-sm font-bold text-center w-full px-2 mt-2 min-h-[2.5rem] line-clamp-2">
                    <span className="text-white">{muni.title}</span>
                  </p>
                </div>

                <button
                  className="absolute top-4 left-4 z-20"
                  onClick={(e) => toggleSavedSeries(e, muni, linkUrl)}
                >
                  {isSaved ? (
                    <FaHeart className="text-red-300" />
                  ) : (
                    <FaRegHeart className="text-red-300" />
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {isOverflowing && (
          <MdChevronRight
            onClick={() => (sliderRef.current.scrollLeft += 500)}
            className="bg-yellow-400 right-0 rounded-full absolute opacity-50 hover:opacity-100 cursor-pointer z-10 hidden group-hover:block"
            size={40}
          />
        )}
      </div>
    </>
  );
};

export default Row;
